/**
 * SourcePanel Component
 * 
 * Left panel showing available clips to add to timeline.
 * Supports event clips (from video tags) and full videos.
 */

import React, { useState, useMemo } from 'react';
import { SourcePanelProps, SourceEventClip } from '../types';
import { eventClipToEditorClip } from '../utils/clipUtils';
import { getStatIcon, getStatColorClasses } from '../../../utils/statIconUtils';

// Tag color function matching EventClipsTab
const getTagColor = (tag: string): string => {
  const colors: Record<string, string> = {
    success: 'bg-green-100 text-green-700',
    attack: 'bg-red-100 text-red-700',
    defense: 'bg-blue-100 text-blue-700',
    goalkeeper: 'bg-yellow-100 text-yellow-700',
    set_piece: 'bg-purple-100 text-purple-700',
    game_flow: 'bg-indigo-100 text-indigo-700',
  };
  return colors[tag] || 'bg-gray-100 text-gray-600';
};

const SourcePanel: React.FC<SourcePanelProps> = ({
  sources,
  onAddClip,
  onAddClips,
  rangeBefore = 5,
  rangeAfter = 5,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filter event clips based on search only (other filters applied at parent level)
  const filteredEventClips = useMemo(() => {
    if (!sources.eventClips) return [];
    
    let clips = sources.eventClips;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      clips = clips.filter((clip) => {
        const label = clip.label?.toLowerCase() || '';
        const statType = clip.metadata?.statType?.toLowerCase() || '';
        const teamName = clip.metadata?.teamName?.toLowerCase() || '';
        const playerName = clip.metadata?.playerName?.toLowerCase() || '';
        const tags = clip.metadata?.tags?.map(t => t.toLowerCase()) || [];
        const tagsMatch = tags.some(tag => tag.includes(query));
        return label.includes(query) || statType.includes(query) || teamName.includes(query) || playerName.includes(query) || tagsMatch;
      });
    }
    
    return clips;
  }, [sources.eventClips, searchQuery]);

  // Toggle selection
  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Select all filtered
  const selectAll = () => {
    const allIds = new Set(filteredEventClips.map((c) => c.id));
    setSelectedIds(allIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Add single clip
  const handleAddClip = (eventClip: SourceEventClip) => {
    const editorClip = eventClipToEditorClip(eventClip, rangeBefore, rangeAfter);
    onAddClip(editorClip);
  };

  // Add selected clips
  const handleAddSelected = () => {
    if (selectedIds.size === 0) return;

    const selectedClips = filteredEventClips
      .filter((c) => selectedIds.has(c.id))
      .map((c) => eventClipToEditorClip(c, rangeBefore, rangeAfter));

    onAddClips(selectedClips);
    setSelectedIds(new Set());
  };

  const hasEventClips = (sources.eventClips?.length || 0) > 0;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-200 bg-white">
        <h3 className="text-sm font-medium text-gray-700">
          Source Media <span className="text-xs text-gray-400 ml-1">({filteredEventClips.length} clips)</span>
        </h3>
      </div>

      {/* Search & Range Controls */}
      {hasEventClips && (
        <div className="p-2 space-y-2 border-b border-gray-200 bg-white">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search clips..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-1.5 pl-8 pr-7 text-xs bg-gray-50 border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 flex items-center justify-center"
                title="Clear search"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>



          {/* Selection Controls */}
          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {selectedIds.size} selected
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={clearSelection}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
                <button
                  onClick={handleAddSelected}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Selected
                </button>
              </div>
            </div>
          )}
          {selectedIds.size === 0 && filteredEventClips.length > 0 && (
            <button
              onClick={selectAll}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Select All ({filteredEventClips.length})
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {!hasEventClips ? (
              <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
                <p className="text-xs text-gray-400">No event clips available</p>
              </div>
            ) : filteredEventClips.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 p-4 text-center">
                <p className="text-xs text-gray-400">No clips match your search</p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredEventClips.map((clip) => (
                  <EventClipItem
                    key={clip.id}
                    clip={clip}
                    isSelected={selectedIds.has(clip.id)}
                    onToggleSelect={() => toggleSelection(clip.id)}
                    onAdd={() => handleAddClip(clip)}
                    rangeBefore={rangeBefore}
                    rangeAfter={rangeAfter}
                  />
                ))}
              </div>
            )}
      </div>
    </div>
  );
};

// Event Clip Item Component
interface EventClipItemProps {
  clip: SourceEventClip;
  isSelected: boolean;
  onToggleSelect: () => void;
  onAdd: () => void;
  rangeBefore: number;
  rangeAfter: number;
}

const EventClipItem: React.FC<EventClipItemProps> = ({
  clip,
  isSelected,
  onToggleSelect,
  onAdd,
  rangeBefore,
  rangeAfter,
}) => {
  const duration = rangeBefore + rangeAfter;
  const colors = getStatColorClasses(clip.metadata?.statKey || null);

  return (
    <div
      className={`flex items-center p-2 rounded-lg cursor-pointer group transition-colors ${
        isSelected ? 'bg-blue-50 ring-1 ring-blue-500' : 'bg-white hover:bg-gray-100 border border-gray-100'
      }`}
      onClick={onToggleSelect}
    >
      {/* Checkbox */}
      <div className="flex-shrink-0 mr-2">
        <div
          className={`w-4 h-4 rounded border flex items-center justify-center ${
            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
          }`}
        >
          {isSelected && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>

      {/* Icon */}
      <div
        className={`w-6 h-6 rounded flex items-center justify-center text-xs flex-shrink-0 ${colors.bg} ${colors.text} ${colors.border} border`}
      >
        {getStatIcon(clip.metadata?.statKey || null)}
      </div>

      {/* Info */}
      <div className="ml-2 flex-1 min-w-0">
        <div className="flex items-center space-x-1">
          <p className="text-xs text-gray-700 truncate font-medium">{clip.label}</p>
          {clip.metadata?.matchMinute !== undefined && (
            <span className="text-xs text-gray-400">{clip.metadata.matchMinute}'</span>
          )}
        </div>
        <p className="text-xs text-gray-400 truncate">
          {[clip.metadata?.teamName, clip.metadata?.playerName].filter(Boolean).join(' • ')} • {duration}s
        </p>
        {clip.metadata?.tags && clip.metadata.tags.length > 0 && (
          <div 
            className="flex gap-1 mt-0.5 overflow-hidden"
            title={clip.metadata.tags.join(', ')}
          >
            {clip.metadata.tags.map((tag, idx) => (
              <span 
                key={idx} 
                className={`text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${getTagColor(tag)}`}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Add Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Add to Timeline"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
};

export default SourcePanel;

